import Avatar from 'react-avatar'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { connect } from 'react-redux'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

import http from '../../../../shared/http'
import { genderOption } from '../../../../shared/const'
import { types } from '../../../../shared/eventEmitter'
import { askConfirm } from '../../../../shared/confirmDialog'
import { optionLabel } from '../../../../shared/optionList'
import { downloadFile, getFormattedDate } from '../../../../shared/helpers'

import Btn from '../../../../UI/Btn'
import BaseModal from '../../../../UI/BaseModal/BaseModal'

import styles from './style.module.scss'

/**
 * Eine Anteilseigner:in — geöffnet aus der Liste der Junior Company oder aus
 * dem gleichnamigen Abschnitt des Firmenprofils.
 *
 * Vorher stand hier das Formular zum Anlegen, nur mit `disabled` an jedem Feld:
 * dreizehn graue Eingabefelder mit Platzhaltern („Geben Sie einen Titel ein"),
 * ein Feld „Anrede: Herr" neben „Geschlecht: Weiblich", und die eigentliche
 * Auskunft — welcher Anteil, wann gezeichnet, welche Erklärung liegt dazu vor —
 * war zwischen den leeren Feldern kaum zu finden. Jetzt ist es eine
 * Leseansicht in derselben Form wie das Fenster einer Mitarbeiter:in: Kopf mit
 * Name und Anteil, Angaben in zwei Spalten, darunter die Erklärung und die
 * Handlungen.
 *
 * Die Handlungen kommen als Nutzlast mit, weil Junior Company und Verwaltung
 * verschiedene Schnittstellen ansprechen (und die Verwaltung nur liest):
 *
 *   eventEmitter.emit(types.openShareholderDetail, {
 *       shareholder,          // Datensatz aus der Liste
 *       downloadUrl: name => …,   // Adresse der Einverständniserklärung
 *       deleteUrl: id => …,       // fehlt sie, gibt es keinen Löschen-Knopf
 *       refresh                   // Liste nach dem Löschen neu holen
 *   })
 */
function ShareholderDetailModal(props) {
	const { t, i18n } = useTranslation()

	const [context, setContext] = useState(null)
	const [shareholder, setShareholder] = useState(null)
	const [isLoading, setLoading] = useState(false)

	const handleOpen = payload => {
		setContext(payload)
		setShareholder(payload?.shareholder || null)
	}

	const handleClose = () => {
		setContext(null)
		setShareholder(null)
	}

	// Vor- und Nachname für die Anzeige; die Titel stehen als eigene Angabe
	// darunter, damit der Kopf bei langen Titeln nicht umbricht.
	const fullName = shareholder ? `${shareholder.firstName || ''} ${shareholder.lastName || ''}`.trim() : ''

	const address = () => {
		if (!shareholder) return ''

		const street = [shareholder.street, shareholder.houseNumber].filter(Boolean).join(' ')
		const city = [shareholder.zipCode, shareholder.city].filter(Boolean).join(' ')

		return [street, city].filter(Boolean).join(', ')
	}

	// Anrede und Geschlecht sind Stammdatenwerte: je nach Anlage deutsch oder
	// englisch gespeichert (siehe shared/optionList).
	const salutationLabel = optionLabel(props.salutations, shareholder?.salutation, i18n.language)

	// Gespeichert ist der Schlüssel (`FEMALE`); beschriftet wird über dieselbe
	// Liste, aus der das Formular seine Auswahl baut.
	const genderLabel = shareholder?.gender
		? t(genderOption.find(item => item.value === shareholder.gender)?.label || shareholder.gender)
		: ''

	const downloadConsent = () => {
		if (!context?.downloadUrl || !shareholder?.consentDocumentHashName) return

		setLoading(true)
		http.get(context.downloadUrl(shareholder.consentDocumentHashName), {
			responseType: 'blob'
		})
			.then(({ data }) => downloadFile(shareholder.consentDocumentHashName, data))
			.finally(() => setLoading(false))
	}

	const remove = async closeModal => {
		const confirmed = await askConfirm({
			title: t('Delete shareholder'),
			text: t('The shareholder and the uploaded consent document will be deleted.'),
			confirmLabel: t('Delete'),
			cancelLabel: t('Cancel'),
			tone: 'danger'
		})
		if (!confirmed) return

		setLoading(true)
		http.delete(context.deleteUrl(shareholder.id))
			.then(() => {
				toast.success(t('Shareholder has been removed successfully.'))
				if (context.refresh) context.refresh()
				closeModal()
			})
			.finally(() => setLoading(false))
	}

	const renderField = (label, value) => (
		<div className={styles.field}>
			<span className={styles.fieldLabel}>{label}</span>
			<span className={styles.fieldValue}>{value || '—'}</span>
		</div>
	)

	return (
		<BaseModal
			fitViewport
			maxWidth={680}
			onOpen={handleOpen}
			onClose={handleClose}
			isLoading={isLoading}
			nameEvent={types.openShareholderDetail}
			title={t('Shareholder details')}
		>
			{({ closeModal }) => {
				if (!shareholder) return null

				return (
					<div className={styles.root}>
						<div className={styles.head}>
							<Avatar round size={56} name={fullName} className={styles.avatar} />
							<div className={styles.headText}>
								<h3 className={styles.name}>{fullName}</h3>
								{/* Der Anteil gehört in den Kopf: Er ist der Grund,
								    warum dieser Datensatz überhaupt existiert. */}
								<div className={styles.badges}>
									<span className={styles.share}>{shareholder.companyShare}&nbsp;€</span>
									<span className={styles.since}>
										{t('Signed on')} {getFormattedDate(shareholder.createdAt)}
									</span>
								</div>
							</div>
						</div>

						{/* Name, Anteil und Datum stehen im Kopf — hier nur, was dort
						    nicht hingehört. */}
						<div className={styles.fields}>
							{renderField(t('salutation'), salutationLabel)}
							{renderField(t('gender'), genderLabel)}
							{!!shareholder.titlePrefixed && renderField(t('Title Prefixed'), shareholder.titlePrefixed)}
							{!!shareholder.titleSuffixed && renderField(t('Title Suffixed'), shareholder.titleSuffixed)}
							{renderField('E-Mail', shareholder.email)}
							{renderField(t('phone'), shareholder.phone)}
							{renderField(t('Address'), address())}
						</div>

						{/* Die Erklärung liegt auch bei den Dokumenten der Junior
						    Company — der Satz sagt es, damit niemand sie hier sucht,
						    wenn sie dort schon abgelegt ist. */}
						{!!context?.downloadUrl && !!shareholder.consentDocumentHashName && (
							<section className={styles.documentBox}>
								<div>
									<strong className={styles.documentTitle}>{t('Consent document')}</strong>
									<p className={styles.documentText}>{t('Consent document hint')}</p>
								</div>
								<Btn
									theme="secondary"
									icon="download"
									iconOrder={0}
									iconSize={{ w: 19, h: 19 }}
									iconColor="#008b9c"
									loading={isLoading}
									onClick={downloadConsent}
								>
									{t('Download')}
								</Btn>
							</section>
						)}

						{!!context?.deleteUrl && (
							<div className={styles.actions}>
								<Btn theme="danger" loading={isLoading} onClick={() => remove(closeModal)}>
									{t('Delete shareholder')}
								</Btn>
							</div>
						)}
					</div>
				)
			}}
		</BaseModal>
	)
}

ShareholderDetailModal.propTypes = {
	salutations: PropTypes.array
}

const mapStateToProps = state => ({
	salutations: state.options.salutations
})

export default connect(mapStateToProps)(ShareholderDetailModal)
